import { SafeUrl } from '@angular/platform-browser';
import { Permissions } from '../dto/permissions';
// date limit
export interface DateLimit {
  start: Date;
  end: Date;
  startAge?: Date;
  endAge?: Date;
}

export interface CardType<T> {
  key?: string;
  name: T;
  value: string;
  pipeType: 'currency' | 'number' | 'percent';
  isFetching: boolean;
  icon: string;
  iconClass?: string;
  currencyCode?: string;
  currencySymbol?: string;
}

export interface ClientReportRequest extends PageRequest {
  type?: string;
}

export interface FilterType {
  startDate?: Date | string;
  endDate?: Date | string;
  date?: Date | string;
  report?: string;
  area?: { id: number; name: string };
  branch?: { id: number; name: string };
  loanOfficer?: { id: number; name: string };
  loanProduct?: { id: number; name: string };
  depositProduct?: { id: number; name: string };
  client?: { id: number; first_name: string; last_name: string };
  depositClient?: { id: number; clientName: string };
  group?: { id: number; name: string };
  limit?: string;
  loanStatus?: any;
  accountNumber?: any;
  state?: string;
}

//Page Requests
export interface PageRequest {
  page: number;
  pageSize: number;
  state: StateTypes['value'];
  sortField: string;
  sortOrder: string;
  filter?: string;
  franchiseeId?: number;
  topClientBy?: string;
  unPaged?: boolean;
  date?: string;
  startDate?: string;
  endDate?: string;
  id?: string;
  savingsAccountType?: string;
  loanAccountType?: string;
  // Scope filters for the client list (0 / omitted = no filter). Typed to allow
  // strings because query params round-trip through the URL as strings.
  areaId?: number | string;
  branchId?: number | string;
  loanOfficerId?: number | string;
  auditType?:
    | 'ClientSavingsAccountAudit'
    | 'ClientLoanAccountAudit'
    | 'ClientAudit';
}

export enum TicketStatus {
  PENDING = 'PENDING',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export interface TicketsPageRequest {
  page: number;
  size: number;
  sortOrder?: string;
  subject?: string;
  loanOfficerId?: number;
  status?: TicketStatus;
  sortField?: string;
}

//Page Requests
export interface ReportPageRequest {
  page: number;
  pageSize: number;
  state?: string;
  status?: string;
  sortField?: string;
  sortOrder?: string;
  filter?: string;
  franchiseeId?: number;
  unPaged?: boolean;
  date?: string;
  startDate?: string;
  sortBalance?: string;
  endDate?: string;
  type?: string;
}

//Transfer Requests
export interface TransferRequest extends PageRequest {
  areaId?: string;
}

export interface PostingRequest extends PageRequest {
  startDate: string;
  endDate: string;
  branchId?: number;
  areaId?: number;
  loanOfficerId?: number;
}

export interface ChartOfAccountRequest extends PageRequest {
  type?: any;
  accountUsage?: any;
  manualJournalEntries?: any;
}
export interface EntryClosureRequest extends PageRequest {
  endDate?: string;
  startDate?: string;
  createdBy?: string;
}
export interface LoanReportRequest extends PageRequest {
  endDate?: string;
  startDate?: string;
  createdBy?: string;
  type?: string;
}
//Custom Requests for Loan
export interface LoanPageRequest {
  page: number;
  pageSize: number;
  state: StateTypes['value'];
  loanState?: StateTypes['value'];
  sortField: string;
  sortOrder: string;
  filter?: string;
  franchiseeId?: number;
  loanAccountType?: 'INDIVIDUAL' | 'CORPORATE';
  // Scope filters (0 / omitted = no filter). Strings allowed because query
  // params round-trip through the URL as strings.
  areaId?: number | string;
  branchId?: number | string;
  loanOfficerId?: number | string;
  // Loan product type: 'STANDARD' | 'ASSET_FINANCING' (omitted = all).
  productCategory?: string;
}

export type LoanAccountType = 'INDIVIDUAL' | 'CORPORATE';

export type SavingsAccountType = 'INDIVIDUAL' | 'CORPORATE';
//Page Requests
export interface PageRequestNoState {
  page: number;
  pageSize: number;
  sortField: string;
  sortOrder: string;
  filter?: string;
  franchiseeId?: number;
}
export interface ClientPageRequest {
  page?: number;
  offset: number;
  limit: number;
  state: string;
  'sort-field': string;
  'sort-order': string;
  franchiseeId?: number;
}

export interface Pagination {
  totalElements: number;
  totalPages: number;
  page: number;
  pageSize: number;
  last: false;
  first: true;
}

// Module actions
export interface ActionButtonType {
  name: string;
  value:
    | 'approve'
    | 'remove'
    | 'modify'
    | 'modify2'
    | 'decline'
    | 'disable'
    | 'reject'
    | 'enable'
    | 'delete'
    | 'activate'
    | 'assign-branches'
    | 'self-disburse'
    | 'approve-loans'
    | 'disburse-loans'
    | 'run'
    | 'cancel'
    | 'reverse'
    | 'blacklist'
    | 'repay'
    | 'waive'
    | 'edit'
    | 'writeOff'
    | 'deposit'
    | 'withdrawal'
    | 'collapse'
    | 'save'
    | 'retry'
    | 'transfer'
    | 'authorize';
  className?: string;
  children?: ActionButtonType[];
  perms: string[];
  visibility?: boolean;
}

// Module states
export interface StateTypes {
  name: string;
  value:
    | 'approved'
    | 'pending'
    | 'pendingEdited'
    | 'editing'
    | 'rejected'
    | 'declined'
    | 'disabled'
    | 'enabled'
    | 'active'
    | 'inactive'
    | 'cancelled'
    | 'cancelledLoan'
    | 'groups'
    | 'clients'
    | 'loans'
    | 'savings'
    | 'summary'
    | 'charges'
    | 'transactions'
    | 'resolved'
    | 'unresolved'
    | 'transfer'
    | 'new_group_posting'
    | 'repayment_schedule'
    | 'document'
    | 'guarantor'
    | 'audits'
    | 'basic'
    | 'ALIMIT'
    | 'BDLIMIT'
    | 'draft'
    | 'AUTHORIZED'
    | 'notification'
    | 'RUNNING'
    | 'operational_indicators'
    | 'financial_indicator'
    | 'user_activity'
    | string;
  perms: string[];
  isActive?: boolean;
  icon?: string;
}

// Menu
export interface Menu {
  label: string;
  link?: string;
  active: boolean;
  icon?: string;
  children?: Array<SubMenu>;
  perm?: string[];
}

interface SubMenu {
  label: string;
  active: boolean;
  link: string;
  perm: string[];
}

// Table
export type DisplayColTypes =
  | 'drag'
  | 'checkbox'
  | 'text'
  | 'boolean'
  | 'object'
  | 'number'
  | 'currency'
  | 'currency_percentile'
  | 'percentile'
  | 'date'
  | 'dateTime'
  | 'customDateSelect'
  | 'customSelectPicker'
  | 'customOptionSelectPicker'
  | 'customConfirmCheckbox'
  | 'customGlAccountSelectPicker'
  | 'customLoanOfficerSelectPicker'
  | 'dateTimeMeridian'
  | 'phone'
  | 'multiValue'
  | 'email'
  | 'id'
  | 'repaymentIndex'
  | 'reversal'
  | 'minusValuesCurrency'
  | 'sumValues'
  | 'chkbxValue'
  | 'customInput'
  | 'btn'
  | 'actionBtn'
  | 'actions'
  | 'checkIcon'
  | 'numberWithDecimal'
  | 'enum'
  | 'status'
  | 'customStatus'
  | 'spacer'
  | 'uppercase';

export interface InitDisplayedCol {
  name: string;
  key: string;
  type: DisplayColTypes;
  value: any;
  perms?: Permissions[];
  footer?: any;
  objectVal?: string | string[];
  values?: string[];
  maxLen?: number;
  decimals?: number; // decimal places for 'number' type columns, defaults to 2
  currencyCodeField?: string; // row field name holding the ISO currency code for per-row symbol resolution
  currencyCode?: string; // static ISO currency code for all rows in this column
  onSort?: boolean;
  toolIcon?: boolean;
  onFilter?: boolean;
  isReversed?: any;
  header?: string;
  tooltip?: string;
  border?: 'l' | 'r' | 'x';
  /* Where a numeric column's values sit; left unless a column opts in. Amounts
     go right so their digits compare down the column, and short figures such
     as rates can be centred under their header. */
  align?: 'left' | 'right' | 'center';
  sticky?: boolean; // pin this column to the left when scrolling horizontally
  class?: string; // e.g 'd-none w-100 px-3', can be space seperated
  expanded?: boolean;
  disable?: boolean;
  status?: string;
  showApplyButton?: any;
  getApplyButtonText?: any;
  onApplyClick?: any;
  customStatus?: any;
  subName?: any;
  // For a 'customStatus' column: render a retry icon beside the status badge.
  // Active on 'failed', greyed/disabled on 'pending', hidden on 'success'.
  // Clicking it emits (retryEmit) with the row from table-ui.
  showRetry?: boolean;
  // For a 'customOptionSelectPicker' column (table3-ui): the choices, either a
  // fixed { label, value } list or a function of the row, plus the hooks the
  // page uses to react to a pick and to flag rows an apply would change.
  options?: any;
  placeholder?: string;
  onOptionChange?: any;
  isMatch?: any;
  getApplyButtonTooltip?: any;
  // Show "Apply to all" and "Apply to Selected" beside the select (or, on a
  // 'customConfirmCheckbox' column, beside a ticked box), as the pending
  // clients table does for loan officers.
  applyToAllOrSelected?: boolean;
  // For a 'customConfirmCheckbox' column (table3-ui): whether a row gets the
  // checkbox, and the text beside it (or in its place when it doesn't apply).
  isApplicable?: any;
  getLabel?: any;
  // Optional: text for an info icon beside the checkbox, and whether the row
  // should show as missing its tick (red box + "Required").
  getTooltip?: any;
  isInvalid?: any;
  // Called when the info icon's tooltip is about to show, to fetch its text.
  onTooltipOpen?: any;
  // table3-ui 'currency' columns: leave the "(₦)" off the header, for tables
  // whose cells already carry the symbol.
  hideHeaderSymbol?: boolean;
}

// Pagination

export interface Pagination {
  firstPage: string;
  lastPage: string;
  nextPage: string;
  pageNumber: number;
  pageSize: number;
  previousPage: string;
  totalPages: number;
  totalRecords: number;
}

export interface Page {
  firstPage: string;
  lastPage: string;
  from?: number;
  to?: number;
  nextPage: string;
  pageNumber: number;
  pageSize: number;
  previousPage: string;
  totalPages: number;
  totalRecords: number;
  pageRequest?: PageRequest;
}
export type PageCtrl = 'prev' | 'first' | 'last' | 'next';

export interface PageCtrls {
  prev: boolean;
  next: boolean;
  first: boolean;
  last: boolean;
}

// Breadcrumb

export interface BreadCrumbLinkType {
  name: string;
  routeTo?: string;
}

// Title

export interface TitleActionBtnType {
  name?: string;
  value: string;
  type: 'btn' | 'menu';
  icon?: string;
  classNames?: string;
  tooltip?: string;
  clickType?: string;
  iconClassNames?: string;
  children?: TitleActionBtnType[];
  perms: string[];
  active?: boolean;
  navStatus?: 'active' | 'not-active';
}

// KeyValuePerm

export interface KeyValuePermType {
  key: string;
  value: string;
  type?: 'currency';
  selected?: boolean;
  perms: string[];
}

// KeyValue

export interface KeyValueType {
  key: string;
  value: string;
  type?: 'currency';
  currencyCode?: string;
  // board2-ui: colours the value, e.g. 'board-value--failed'
  valueClass?: string;
}

// Image-Upload
export interface FileDetail {
  name?: string;
  size?: number;
  file: File | string;
  type?: string;
  url: any;
}

// ID & NAME

export interface IdNameType {
  id: number;
  name: string;
  extra?: {
    id: number;
    name: string;
  };
}

// Item

export interface ItemType {
  title: string | number;
  subtitle?: string;
  icon?: string;
}
export interface reportSummaryType {
  label: string;
  type: 'currency' | 'number' | 'date';
  value: any;
}

// MultiButton
export interface MultiButtonType {
  name: string;
  classNames: string;
  value: string;
  perms: string[];
}

// Reports Input Type
export enum ReportInputTypes {
  INACTIVE_SINCE = 'inactive_since',
  START_DATE = 'startDate',
  DATE = 'date',
  END_DATE = 'endDate',
  REPORT = 'report',
  AREA = 'areaId',
  BRANCH = 'branchId',
  GROUP = 'groupId',
  LIMIT = 'limit',
  LOAN_OFFICER = 'loanOfficerId',
  LOAN_PRODUCT = 'productId',
  CLIENT_STATUS = 'status',
  LOAN_STATUS = 'loanStatus',
  LOAN_STATE = 'loanState',
  DEPOSIT_PRODUCT = 'depositProductId',
  CLIENT = 'clientId',
  DEPOSIT_CLIENT = 'depositClientId',
  ACCOUNT_NUMBER = 'accountNumber',
  STATE = 'state',
  SORT = 'sort',
  PORTFILIO_AT_RISK = 'portfolio',
  LOAN_CLASSIFICATION = 'LOAN_CLASSIFICATION',
  LOAN_RISK_CLASSIFICATION = 'LOAN_RISK_CLASSIFICATION',
  ACCOUNT_STATE = 'ACCOUNT_STATE',
  CUSTOMER_TYPE = 'CUSTOMER_TYPE',
  CURRENCY = 'currency',
}

// Searchable Select Input Type
export interface SearchableSelectTypes {
  label: string;
  value: any;
  fundSources?: any[];
  disbursementGLs?: any[];
  repaymentGLs?: any[];
  productCategory?: string;
  currency?: string;
}
// move to dependencies
// Account Officer
export interface LoanOfficerType {
  id: number;
  name: string;
  extra: { id: number; name: string };
}

// Response Object
export interface Res {
  code: number;
  message: string;
  successful: boolean;
}

export interface API<T> {
  code: number;
  message: string;
  successful: boolean;
  data: T;
}
// Pagination Response
export interface PaginationRes {
  total: number;
  page: number;
  pageSize: number;
  totalElements: number;
  last: false;
  first: true;
}

// Approve, Reject, Enable, Disable Action Payload
export interface TogglePayload {
  ids?:
    | number[]
    | {
        id: number;
        loanOfficerId?: string;
        loan_limit?: number;
      }[];
  id?: number;
  remark?: string;
  reason?: string;
  enable?: boolean;
  setMultipleRunningLoans?: boolean;
  message?: string;
  accountId?: number;
  amount?: number;
  state?: StateTypes['value'];
  disbursementDate?;
  firstRepaymentDate?;
}

export interface CancelLoanPayload {
  ids: number[];
  remark?: string;
  reason?: string;
  accountId?: number;
}

export interface SignatoryOpts {
  name: string;
  value: string;
}
export interface Sign {
  name: string;
  value: string;
  id: number;
}
export interface Signature {
  id: number;
  signs: Sign[];
}

export interface GlAccount {
  id: number;
  name: string;
}

export interface AccountingCategory {
  name: string;
  row: AccountingTransaction[];
}

export interface AccountingTransaction {
  eventName: string;
  eventNameList: string[];
  debitGlId: number;
  creditGlId: number;
  creditGlAccountList: GlAccount[];
  debitGlAccountList: GlAccount[];
  debitGlName: string;
  creditGlName: string;
  eventMapping: string;
  eventMappingList: string[];
  label: string;
  groupLabel: string;
  chargeId?: number;
  chargeType?: string; // populated from fee-charge-config
  latePaymentChargeId?: number;
  siblingCharge?: boolean;
  feeValue?: string; // populated from fee-charge-config
}

export interface ChartFilterControlsInterface {
  name: ReportInputTypes;
  type: keyof typeof ReportInputTypes;
  position: 'right' | 'left';
  value?: any;
}

export interface LineChartSetup {
  xAxis: {
    label: string;
    value: string;
  };
  yAxis: {
    label: string;
    color: string;
    value: string;
    useAverage?: boolean;
  }[];
}

export interface Closure {
  approval_state: 'APPROVED' | 'REJECTED' | 'PENDING';
  initiator: string;
  treatedBy: string;
  treated_on: string;
  reserve_account_id: number;
  reserveAccount: string;
  initiator_id: number;
  closure_year: string;
  last_update_time: string;
  treated_by_id: number;
  id: number;
  transaction_year: string;
  initiated_on: string;
  canUndo?: boolean;
}

export interface Message {
  id: number;
  ticketId: number;
  isLocal?: boolean;
  senderId: number;
  senderName: string;
  message: string;
  sentAt: string | Date;
  attachment?: string;
}

export type TicketInfo = {
  [key: number]: { unreadCount: number; readCount: number };
};

export interface Ticket {
  id: number;
  publicId: number;
  createdById: number;
  subject: string;

  messages: Message[];
  status: string;
  createdAt: string;
  loanOfficer: string;
}
export interface TicketPagination {
  content: Ticket[];
  pageable: {
    sort: {
      sorted: boolean;
      empty: boolean;
      unsorted: boolean;
    };
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  last: boolean;
  totalElements: number;
  totalPages: number;
  first: boolean;
  size: number;
  number: number;
  sort: {
    sorted: boolean;
    empty: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  empty: boolean;
}

export interface TicketRes {
  data: TicketPagination;
  message: string;
  successful: boolean;
  code: number;
}
export interface MessageRes {
  data: Message[];
  message: string;
  successful: boolean;
  code: number;
}
